import React from 'react';
import PropTypes from 'prop-types';

import Settings from 'resources/settings';
import Utils from 'utils';
import styles from './AccordionItems.module.css';
import commonStrings from '../../resources/commonStrings';
import { useAnalytics } from 'hooks/useAnalytics';

const comprehensiveDataFile = 'COMPREHENSIVE_DATA';
const combinedDataFile = 'COMBINED_STUDENT_AND_ASSESSMENT_RESULTS';

const dataExportHistoryShape = PropTypes.shape({
  editedDate: PropTypes.number.isRequired,
  reportId: PropTypes.number.isRequired,
  typeDescription: PropTypes.oneOf([comprehensiveDataFile, combinedDataFile]).isRequired,
  termId: PropTypes.number.isRequired,
  createdDate: PropTypes.number,
  fileSize: PropTypes.number,
}).isRequired;

const propTypes = {
  containingTab: PropTypes.string.isRequired,
  identifier: PropTypes.string.isRequired,
  exportHistory: PropTypes.arrayOf(dataExportHistoryShape).isRequired,
};

/**
 * This represents the dropdownItem which shows the data export history. This
 * is represented via 2 items
 * 1 - An optional failed export message which can be removed by clicking an x icon
 * 2 - A required link to the last good export which can be downloaded via a link
 *
 * Styling:
 * -Clicking the optional failed export x icon will cause it to collapse.
 */
function DataExportScheduler({ containingTab, identifier, exportHistory }) {
  const { handleItemClick } = useAnalytics(`rlp-${containingTab}-reports`, 'downloaded-dex', identifier, '');
  const { handleItemClick: handleScheduleDEXClick, handleKeystroke: handleScheduleDexKeystroke } = useAnalytics(
    `rlp-${containingTab}-reports`,
    'navigate',
    'schedule-data-export',
    ''
  );

  // If reporting gateway is down we may get no data back so handle null/undefined data
  const exportHistoryPayload = exportHistory ? exportHistory : [];

  const downloadLink = (lastSuccessfulItem) => {
    const linkText = commonStrings.dataExport.downloadSuccessfulDataExport(
      Utils.formatDate(lastSuccessfulItem.createdDate),
      lastSuccessfulItem && lastSuccessfulItem.typeDescription === comprehensiveDataFile
    );

    return (
      <li key="exportSuccess" className={styles['line-item']}>
        <a
          className={styles['download-link']}
          href={`${Settings.ADMIN_BASE_PATH}/report/download/cdf/${lastSuccessfulItem.reportId}`}
          onClick={handleItemClick}
        >
          {linkText}
        </a>
        <span className={styles['download-file-size']}>
          {Utils.getReadableFileSizeString(lastSuccessfulItem.fileSize)}
        </span>
      </li>
    );
  };

  const items = [];
  const linkDataExportScheduler = `${Settings.ADMIN_BASE_PATH}/report/map/comprehensiveDataFile.seam`;
  /* Wrapped link/text in a span to compensate for
   *  .line-item: justify-content: space-between;
   */
  const listItemScheduleDataExportLink = (
    <li key="DataExportLink" className={styles['line-item-inactive']}>
      <div>
        <span>{commonStrings.dataExport.youCan}</span>
        <a
          href={linkDataExportScheduler}
          className={styles['download-link-only']}
          onClick={handleScheduleDEXClick}
          onKeyUp={handleScheduleDexKeystroke}
          target="_blank"
        >
          {commonStrings.dataExport.scheduleAnExport}
        </a>
      </div>
    </li>
  );
  if (exportHistoryPayload.length > 0) {
    items.push(downloadLink(exportHistoryPayload[0]), listItemScheduleDataExportLink);
  } else {
    const listItemNoDataText = (
      <li key="NoData" className={styles['line-item-inactive']}>
        {commonStrings.dataExport.noDataAvailable}
      </li>
    );
    items.push(listItemNoDataText, listItemScheduleDataExportLink);
  }

  return (
    <ul key={`${containingTab}-${identifier}`} className={styles['list-items']}>
      {items}
    </ul>
  );
}

DataExportScheduler.propTypes = propTypes;

export default DataExportScheduler;
